import {
  FieldError,
  Label,
  RadioButton,
  RadioField,
  RadioGroup as AriaRadioGroup,
  Text,
  type RadioGroupProps as AriaRadioGroupProps,
} from 'react-aria-components';

import styles from './RadioGroup.module.css';

export interface RadioOption {
  id: string;
  label: string;
  /** One line under the label, e.g. what the choice means. */
  description?: string;
  isDisabled?: boolean;
}

export interface RadioGroupProps extends Omit<
  AriaRadioGroupProps,
  'children' | 'className' | 'style' | 'onChange'
> {
  /** Always visible, above the options. */
  label: string;
  /** Shown in the order given. A handful of choices; more than that wants a search picker. */
  options: readonly RadioOption[];
  onChange?: (value: string) => void;
  description?: string;
  /** What is wrong and how to fix it. Setting it marks the group invalid. */
  errorMessage?: string;
}

/**
 * One choice from a short, visible list: sex, visit type, first queue, consent decision.
 * Every option is on screen, so nothing is hidden in a dropdown.
 */
export function RadioGroup({
  label,
  options,
  description,
  errorMessage,
  orientation = 'vertical',
  isInvalid,
  onChange,
  ...rest
}: RadioGroupProps) {
  return (
    <AriaRadioGroup
      {...rest}
      {...(onChange === undefined ? {} : { onChange })}
      orientation={orientation}
      isInvalid={isInvalid === true || errorMessage !== undefined}
      className={styles.group}
    >
      <Label className={styles.label}>{label}</Label>
      <div className={styles.options} data-orientation={orientation}>
        {options.map((option) => (
          <RadioField
            key={option.id}
            value={option.id}
            className={styles.option}
            {...(option.isDisabled === true ? { isDisabled: true } : {})}
          >
            <RadioButton className={styles.radio}>
              <span className={styles.circle} aria-hidden="true" />
              <span>{option.label}</span>
            </RadioButton>
            {option.description === undefined ? null : (
              <Text slot="description" className={styles.optionDescription}>
                {option.description}
              </Text>
            )}
          </RadioField>
        ))}
      </div>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </AriaRadioGroup>
  );
}
